package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * Picso limits according to docs/product/PICSO_PLAN.md.
 */
object PicsoLimits {
    const val CAPTION_MAX = 100
    const val MAX_POSTS_PER_24H = 10
    const val EMOJI_MAX = 16
    const val EXPIRY_HOURS = 24L
    const val MODERATION_RETENTION_DAYS = 30L
}

@Serializable
data class PicsoAuthor(
    @SerialName("id") val id: Uuid,
    @SerialName("username") val username: String,
    @SerialName("display_name") val displayName: String,
    @SerialName("avatar_url") val avatarUrl: String? = null,
)

@Serializable
data class PicsoPost(
    @SerialName("id") val id: Uuid,
    @SerialName("author") val author: PicsoAuthor,
    @SerialName("media_id") val mediaId: Uuid,
    @SerialName("media_url") val mediaUrl: String,
    @SerialName("caption") val caption: String? = null,
    @SerialName("published_at") val publishedAt: Timestamp,
    @SerialName("expires_at") val expiresAt: Timestamp,
    @SerialName("views_count") val viewsCount: Int = 0,
    @SerialName("unique_viewers_count") val uniqueViewersCount: Int = 0,
    @SerialName("reactions_count") val reactionsCount: Int = 0,
    @SerialName("my_reaction") val myReaction: String? = null,
    @SerialName("is_saved") val isSaved: Boolean = false,
    @SerialName("is_mine") val isMine: Boolean = false,
    @SerialName("server_time") val serverTime: Timestamp? = null,
)

@Serializable
data class PicsoFeedPage(
    @SerialName("items") val items: List<PicsoPost>,
    @SerialName("page_info") val pageInfo: PageInfo,
    @SerialName("server_time") val serverTime: Timestamp? = null,
)

@Serializable
data class PicsoReactionSummary(
    @SerialName("picso_id") val picsoId: Uuid,
    @SerialName("reactions_count") val reactionsCount: Int,
    @SerialName("my_reaction") val myReaction: String? = null,
)

@Serializable
data class PicsoSaveStatus(
    @SerialName("picso_id") val picsoId: Uuid,
    @SerialName("is_saved") val isSaved: Boolean,
)

@Serializable
data class PicsoAnalytics(
    @SerialName("picso_id") val picsoId: Uuid,
    @SerialName("views_count") val viewsCount: Int,
    @SerialName("unique_viewers_count") val uniqueViewersCount: Int,
    @SerialName("reactions_count") val reactionsCount: Int,
    @SerialName("published_at") val publishedAt: Timestamp,
    @SerialName("expires_at") val expiresAt: Timestamp,
)

@Serializable
data class CreatePicsoRequest(
    @SerialName("client_id") val clientId: Uuid,
    @SerialName("upload_id") val uploadId: Uuid,
    @SerialName("caption") val caption: String? = null,
)

@Serializable
data class SetPicsoReactionRequest(
    @SerialName("emoji") val emoji: String,
)

@Serializable
data class RecordPicsoViewRequest(
    @SerialName("client_id") val clientId: Uuid,
)

@Serializable
data class PicsoShareRequest(
    @SerialName("client_id") val clientId: Uuid,
    @SerialName("conversation_id") val conversationId: Uuid,
)

/** Expiring reference only: never contains photo bytes or a public storage URL. */
@Serializable
data class PicsoReference(
    @SerialName("id") val id: Uuid,
    @SerialName("expires_at") val expiresAt: Timestamp,
)
